import PropTypes from 'prop-types';
import { createRef, Component } from 'react';
import { get, debounce } from 'lodash';

import { isStaleIntegrationIframe } from 'utils/checkEnvironment';
import { KEYS } from 'components/utils/keys';

import * as styled from './styles/overlay';

export class Overlay extends Component {
  constructor(props) {
    super(props);

    this.overlayRef = createRef();
    this.viewRef = createRef();
    this.state = {
      height: props.fitScreen ? window.innerHeight : undefined,
    };
    this.debouncedChangeHeight = debounce(() => {
      requestAnimationFrame(() => {
        this.changeHeight();
      });
    }, 50);
  }

  componentDidMount() {
    const { current } = this.overlayRef;

    document.addEventListener('keydown', this.onEscape);
    if (!current.contains(document.activeElement)) {
      current.focus();
    }
    current.removeAttribute('tabindex');
    window.addEventListener('resize', this.changeHeight);
    this.observeResize(this.viewRef.current);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onEscape);
    window.removeEventListener('resize', this.changeHeight);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.debouncedChangeHeight.cancel();
  }

  onEscape = ({ keyCode }) => {
    if (keyCode === KEYS.ESCAPE) {
      this.onCloseOverlay();
    }
  };

  onCloseOverlay = (e) => {
    const { closeOverlay, noCloseOnBackgroundClick, noClose } = this.props;

    if (!e || e.currentTarget === e.target) {
      return noClose || noCloseOnBackgroundClick ? null : closeOverlay();
    }
    return false;
  };

  onClickArrow = (e, fn) => {
    e.stopPropagation();
    fn();
  };

  observeResize = (el) => {
    if (!el) return;
    this.resizeObserver = new ResizeObserver((entries) => {
      if (!get(entries, '0.contentRect.height')) return;
      this.debouncedChangeHeight();
    });

    this.resizeObserver.observe(el);
  };

  changeHeight = () => {
    const { fitScreen } = this.props;
    const height = fitScreen ? { height: window.innerHeight } : {};
    this.setState({
      ...height,
      viewHeight: this.viewRef.current?.getBoundingClientRect().height,
    });
  };

  closeOverlayUI = () => {
    const { noClose, closeOverlay } = this.props;
    return noClose ? null : <styled.Close fn={closeOverlay} />;
  };

  render() {
    const {
      padding,
      children,
      isLight,
      pullRight,
      noGlobalScrollLock,
      gotoLeft = false,
      gotoRight = false,
      gotoLeftFn,
      gotoRightFn,
      noCloseOnBackgroundClick,
      zIndex,
      fitScreen,
      backgroundUrl,
      className,
    } = this.props;
    const { height, viewHeight } = this.state;

    const leftArrow = gotoLeft ? (
      <styled.Prev prev isSmall fn={(e) => this.onClickArrow(e, gotoLeftFn)} />
    ) : null;

    const rightArrow = gotoRight ? (
      <styled.Next next isSmall fn={(e) => this.onClickArrow(e, gotoRightFn)} />
    ) : null;

    return (
      <styled.Overlay
        ref={this.overlayRef}
        tabIndex="0"
        isLight={isLight}
        alignItems="center"
        justifyContent="center"
        padding={padding}
        pullRight={pullRight}
        onClick={noCloseOnBackgroundClick ? undefined : this.onCloseOverlay}
        isStaleIntegration={isStaleIntegrationIframe(window)}
        zIndex={zIndex}
        height={height}
        backgroundUrl={backgroundUrl}
        className={className}
        fitScreen={fitScreen}
      >
        {leftArrow}

        <styled.View
          fitScreen={fitScreen}
          height={viewHeight}
          ref={this.viewRef}
        >
          {this.closeOverlayUI()}

          {children}
        </styled.View>

        {rightArrow}

        {noGlobalScrollLock || <styled.GlobalScrollLock />}
      </styled.Overlay>
    );
  }
}

Overlay.propTypes = {
  closeOverlay: PropTypes.func,
  gotoLeftFn: PropTypes.func,
  gotoRightFn: PropTypes.func,
  padding: PropTypes.string,
  noClose: PropTypes.bool,
  noCloseOnBackgroundClick: PropTypes.bool,
  noGlobalScrollLock: PropTypes.bool,
  isLight: PropTypes.bool,
  gotoLeft: PropTypes.bool,
  gotoRight: PropTypes.bool,
  pullRight: PropTypes.string,
  zIndex: PropTypes.number,
  children: PropTypes.node,
  fitScreen: PropTypes.bool,
  backgroundUrl: PropTypes.string,
  className: PropTypes.string,
};

Overlay.defaultProps = {
  isLight: false,
  noClose: false,
  noCloseOnBackgroundClick: false,
  noGlobalScrollLock: false,
};
